import { FC, useContext } from 'react';

import EntityRecordUI from '../atoms/EntityRecordUI';
import { EntitiesContext } from '../../providers/artifacts/EntitiesContext';
import { UIElement, UIEntityReference } from '#types/System.types';
import { useProtectedFields } from '../../use/useProtectedFields';

/**
 * Props for the EntityUI component.
 */
interface EntityUIProps {
  /**
   * The UI element configuration containing entity reference metadata.
   * Should include entityGroupId if referencing a specific record group.
   */
  uiElement: UIElement;
  /** Whether the entity selector should be in read-only/disabled state */
  disabled?: boolean;
}

/**
 * EntityUI - Renders a labeled entity record selector with context-aware configuration.
 *
 * **Purpose:**
 * Thin wrapper around EntityRecordUI that extracts configuration from UI element
 * definition and derives the label from entity metadata. EntityRecordUI handles
 * all state management, persistence, label rendering, and context detection internally.
 *
 * **Context Requirements:**
 * - EntitiesContext: Provides entity metadata for labels
 * - EntityRecordUI: Internally detects FormContext or EntityContext for persistence
 *
 * @example
 * <EntityUI uiElement={uiElement} />
 */
const EntityUI: FC<EntityUIProps> = ({ uiElement, disabled = false }) => {
  const { entities } = useContext(EntitiesContext);
  const { isEntityReferenceProtected } = useProtectedFields();

  const entityReference = uiElement as UIEntityReference;
  const entityId = entityReference.entityId;
  const entityGroupId = entityReference.entityGroupId;

  if (!entityId) return null;

  const entityRefId = `${entityId}:${entityGroupId || ''}`;
  const isProtected = isEntityReferenceProtected(entityRefId);
  const effectiveDisabled = disabled || isProtected;

  const referencedEntity = entities[entityId];
  const entityGroup = entityGroupId ? referencedEntity?.recordGroups?.[entityGroupId] : undefined;
  const entityLabel = entityGroup?.description?.shortLabel
    || referencedEntity?.description?.shortLabel 
    || entityId;

  return (
    <EntityRecordUI
      entityId={entityId}
      entityGroupId={entityGroupId}
      disabled={effectiveDisabled}
      label={entityLabel}
      required={entityReference.required}
      triggerSearchSync={entityReference.triggerSearchSync}
    />
  );
};

export default EntityUI;